import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getRoles, updateUser } from '../../services/iam';
import { User, Role } from '../../types/iam';
import { toast } from '@/lib/toast';
import { Button, Modal, Select } from '@/components/ds-zta';

interface Props {
  user: User;
  onClose: () => void;
}

const UpdateUserModal: React.FC<Props> = ({ user, onClose }) => {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<string>(user.primary_role || '');

  const { data: rolesResponse } = useQuery(['roles', 'primary'], () =>
    getRoles({ limit: 100, primary_only: true })
  );

  const mutation = useMutation(
    (roleId: string | null) => {
      return updateUser(user.id, { primary_role: roleId || undefined });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User updated successfully');
        onClose();
      },
      onError: (err: any) => {
        toast.error(`Failed to update user: ${err.message}`);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(selectedRole || null);
  };

  const roles = rolesResponse?.data || [];

  return (
    <Modal
      open
      title="Update User"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="update-user-form" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Updating...' : 'Update'}
          </Button>
        </>
      }
    >
      <form id="update-user-form" onSubmit={handleSubmit} className="space-y-4 text-[var(--d2d-text-primary)]">
        <Select
          id="primary_role"
          name="primary_role"
          label="Primary Role"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          <option value="">Select Role</option>
          {roles.map((role: Role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
      </form>
    </Modal>
  );
};

export default UpdateUserModal;
